import type { Mandate, MandateStatus } from "@/types/mandate";
import { computeEmi } from "@/pages/retailer/create-mandate/emi";

export interface ProductProfile {
  category: string;
  brand: string;
  model: string;
  price: number;
  down: number;
  rate: number;
  tenure: number;
  invoiceDate: string;
  description: string;
}

const PROFILES: ProductProfile[] = [
  { category: "Mobile Phone", brand: "Samsung", model: "Galaxy S24 Ultra", price: 104999, down: 15000, rate: 18, tenure: 12, invoiceDate: "18 Aug 2026", description: "Titanium 512GB, dual SIM smartphone with 200MP camera" },
  { category: "Mobile Phone", brand: "Apple", model: "iPhone 15", price: 79900, down: 10000, rate: 16, tenure: 12, invoiceDate: "20 Aug 2026", description: "128GB, A16 Bionic chip, 6.1-inch Super Retina display" },
  { category: "Mobile Phone", brand: "Xiaomi", model: "Redmi Note 13 Pro", price: 24999, down: 3000, rate: 14, tenure: 6, invoiceDate: "12 Aug 2026", description: "8GB RAM, 256GB storage, 108MP triple camera" },
  { category: "Laptop", brand: "HP", model: "Pavilion 15", price: 62990, down: 8000, rate: 18, tenure: 18, invoiceDate: "25 Aug 2026", description: "Core i5, 16GB RAM, 512GB SSD, 15.6-inch FHD display" },
  { category: "Laptop", brand: "Dell", model: "Inspiron 5430", price: 54990, down: 7000, rate: 16, tenure: 12, invoiceDate: "22 Aug 2026", description: "Core i5, 16GB RAM, 512GB NVMe SSD with backlit keyboard" },
  { category: "Two Wheeler", brand: "Honda", model: "Activa 6G", price: 85000, down: 10000, rate: 14, tenure: 18, invoiceDate: "15 Aug 2026", description: "110cc scooter with LED headlamp and silent start" },
  { category: "Two Wheeler", brand: "Bajaj", model: "Pulsar N160", price: 124000, down: 20000, rate: 15, tenure: 24, invoiceDate: "28 Aug 2026", description: "160cc motorcycle with ABS and digital instrument cluster" },
  { category: "Consumer Durable", brand: "Samsung", model: "Refrigerator 324L", price: 38000, down: 4000, rate: 14, tenure: 9, invoiceDate: "10 Aug 2026", description: "Double door frost-free refrigerator, 3-star rated" },
  { category: "Consumer Durable", brand: "LG", model: "Washing Machine 7kg", price: 32500, down: 3500, rate: 16, tenure: 12, invoiceDate: "16 Aug 2026", description: "Front load washing machine with steam technology" },
  { category: "Furniture", brand: "Wakefit", model: "3-Seater Sofa", price: 45000, down: 5000, rate: 15, tenure: 12, invoiceDate: "19 Aug 2026", description: "Premium fabric 3-seater sofa set with foam cushioning" },
  { category: "Medical Equipment", brand: "Mediplus", model: "CPAP Pro 20", price: 28000, down: 3000, rate: 12, tenure: 12, invoiceDate: "11 Aug 2026", description: "Auto CPAP machine for sleep therapy with humidifier" },
  { category: "Other", brand: "Kaff", model: "Chimney 90cm", price: 18000, down: 2000, rate: 12, tenure: 6, invoiceDate: "08 Aug 2026", description: "Auto-clean kitchen chimney with motion sensor" },
];

const ID_TYPES = ["Aadhaar card", "PAN card", "Voter ID", "Driving Licence", "Passport"];
const ADDRESS_TYPES = ["Aadhaar card", "Electricity bill", "Rent agreement", "Passport"];

const DOBS = ["12 Mar 1985", "04 Jun 1990", "21 Jan 1982", "15 Apr 1988", "28 Sep 1993", "09 Nov 1986", "17 Feb 1992", "30 Jul 1989"];
const ADDRESSES = [
  "14, Main Bazaar Road, Karol Bagh",
  "22, Sector 62, Hoshiarpur Road",
  "8, Cyber Hub, DLF Phase 2",
  "45, MI Road, Near Clock Tower",
  "12, Hazratganj Market",
  "56, MG Road, Fort",
  "9, Park Street, Near Metro",
  "31, Lakeview Layout",
];
const CITIES = ["Delhi", "Noida", "Gurugram", "Jaipur", "Lucknow", "Mumbai", "Kolkata", "Bengaluru"];
const STATES = ["Delhi", "Uttar Pradesh", "Haryana", "Rajasthan", "Uttar Pradesh", "Maharashtra", "West Bengal", "Karnataka"];
const PINCODES = ["110005", "201301", "122002", "302001", "226001", "400001", "700016", "560034"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Deterministic V4 status redistribution (approx 50% ACTIVE, 15% PENDING_AUTH,
// 10% CREATED, 8% COMPLETED, 7% TERMINATED, 5% CANCELLED, 3% PAUSED, 2% HALTED).
const STATUS_CYCLE: MandateStatus[] = [
  ...Array(20).fill("ACTIVE" as const),
  ...Array(6).fill("PENDING_AUTH" as const),
  ...Array(4).fill("CREATED" as const),
  ...Array(3).fill("COMPLETED" as const),
  ...Array(3).fill("TERMINATED" as const),
  ...Array(2).fill("CANCELLED" as const),
  ...Array(1).fill("PAUSED" as const),
  ...Array(1).fill("HALTED" as const),
];

function nextStatus(index: number): MandateStatus {
  return STATUS_CYCLE[index % STATUS_CYCLE.length];
}

export function authModeLabel(mode: string | undefined): string {
  if (mode === "UPI_AUTO_PAY") return "UPI Autopay";
  return "eNACH";
}

function pad(n: number, len: number): string {
  return String(n).padStart(len, "0");
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function makeDob(seed: number): string {
  const d = DOBS[seed % DOBS.length];
  return d;
}

function makePan(seed: number): string {
  return `ABCDE${pad(1000 + seed, 4)}P`;
}

function makeAadhaar(seed: number): string {
  return `XXXX XXXX ${pad(2000 + seed, 4)}`;
}

export function enrichMandate(base: Mandate, index: number): Mandate {
  const profile = PROFILES[index % PROFILES.length];
  const emi = computeEmi(String(profile.price), String(profile.down), String(profile.rate), String(profile.tenure));

  const serialPrefix =
    profile.category === "Mobile Phone"
      ? "IMEI"
      : profile.category === "Laptop" || profile.category === "Two Wheeler"
        ? "SER"
        : "SN";
  const serialNumber = `${serialPrefix}-${pad(880000 + index * 137, 6)}`;

  const startMonth = index % 12;
  const startDay = pad((index % 27) + 1, 2);

  return {
    ...base,
    status: nextStatus(index),
    productCategory: profile.category,
    brand: profile.brand,
    model: profile.model,
    serialNumber,
    invoiceNumber: `INV-${pad(34000 + index * 9, 6)}`,
    invoiceDate: profile.invoiceDate,
    productDescription: profile.description,
    productPrice: profile.price,
    downPayment: profile.down,
    financedAmount: emi ? round2(emi.financed) : profile.price - profile.down,
    interestRate: profile.rate,
    tenure: profile.tenure,
    totalInterest: emi ? round2(emi.interest) : 0,
    totalPayable: emi ? round2(emi.payable) : profile.price - profile.down,
    monthlyEmi: emi ? round2(emi.emi) : round2((profile.price - profile.down) / profile.tenure),
    amount: emi ? round2(emi.emi) : round2((profile.price - profile.down) / profile.tenure),
    customerDob: makeDob(index),
    customerPan: makePan(index),
    customerAadhaar: makeAadhaar(index),
    customerAddress: ADDRESSES[index % ADDRESSES.length],
    customerCity: CITIES[index % CITIES.length],
    customerState: STATES[index % STATES.length],
    customerPincode: PINCODES[index % PINCODES.length],
    idProofType: ID_TYPES[index % ID_TYPES.length],
    idProofFileName: `id-proof-${pad(100 + index, 3)}.jpg`,
    addressProofType: ADDRESS_TYPES[index % ADDRESS_TYPES.length],
    addressProofFileName: `address-proof-${pad(100 + index, 3)}.jpg`,
    customerPhotoFileName: `customer-photo-${pad(100 + index, 3)}.jpg`,
    signatureFileName: `signature-${pad(100 + index, 3)}.png`,
    startDate: `${startDay} ${MONTHS[startMonth]} 2026`,
  };
}

export const enrichedMandates: Mandate[] = [];

export function findEnriched(_ref: string): Mandate | null {
  return null;
}

export function maskAadhaar(value: string | undefined): string {
  if (!value) return "—";
  if (value.includes("XXXX")) return value;
  const digits = value.replace(/\D/g, "");
  if (digits.length < 4) return value;
  return `XXXX XXXX ${digits.slice(-4)}`;
}

export function maskAccount(value: string): string {
  if (!value) return "—";
  if (value.length <= 4) return value;
  return "X".repeat(value.length - 4) + value.slice(-4);
}